/* @refresh reset */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { toast } from 'sonner'
import { workflowsApiClient } from '@/lib/api/workflows.client'
import { transformWorkflowToHandoffRule } from '@/lib/utils/workflow-transformers'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  RelationshipAccessLevel,
  type WorkflowWithActionGroups,
  type ActionGroup,
  type Property,
  type WeeklySchedule,
  OfficeHoursMode,
} from '@super/shared-types'

// UI-specific HandoffRule interface that the components expect
export interface HandoffRule {
  id: string
  title: string
  selectionCriteria: string
  propertyIds: string[]
  propertyGroupIds: string[]
  propertyNames: string[]
  groupTypes: RelationshipAccessLevel[]
  officeHours: OfficeHoursMode
  customOfficeHoursSchedule?: WeeklySchedule
  handoffInstructions: string
  actionGroups: ActionGroup[]
  createdAt: Date
  updatedAt: Date
}

interface HandoffWorkflowsContextType {
  // State
  handoffRules: HandoffRule[]
  loading: boolean

  // Actions
  refreshWorkflows: () => Promise<void>
  createWorkflow: (workflow: WorkflowWithActionGroups) => Promise<void>
  updateWorkflow: (
    id: string,
    workflow: WorkflowWithActionGroups,
  ) => Promise<void>
  deleteWorkflow: (id: string) => Promise<void>
  duplicateWorkflow: (id: string) => Promise<void>

  // Helpers
  findRuleById: (id: string) => HandoffRule | undefined
}

const HandoffWorkflowsContext = createContext<
  HandoffWorkflowsContextType | undefined
>(undefined)

interface HandoffWorkflowsProviderProps {
  children: ReactNode
  properties: Property[] // Pass properties for transformation
}

export function HandoffWorkflowsProvider({
  children,
  properties,
}: HandoffWorkflowsProviderProps) {
  const [handoffRules, setHandoffRules] = useState<HandoffRule[]>([])
  const [loading, setLoading] = useState(true)
  const { currentCompany, isSuperAdmin } = useAppAuth()

  // Fetch workflows from API
  const refreshWorkflows = async () => {
    if (!currentCompany?.id) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      // Use admin API if super admin, otherwise use regular API
      const workflows = isSuperAdmin
        ? await workflowsApiClient.getAllAdminHandoffWorkflowsForCompany(
            currentCompany.id,
          )
        : await workflowsApiClient.getHandoffWorkflows()

      const transformedRules = workflows.map(workflow =>
        transformWorkflowToHandoffRule(workflow, properties),
      )
      setHandoffRules(transformedRules)
    } catch (error) {
      console.error('Error loading handoff workflows:', error)
      toast.error('Failed to load handoff rules')
      setHandoffRules([])
    } finally {
      setLoading(false)
    }
  }

  // Create new workflow (workflow is already created via API in the form, just update local state)
  const createWorkflow = async (workflow: WorkflowWithActionGroups) => {
    try {
      // Optimistically add to local state
      const newRule = transformWorkflowToHandoffRule(workflow, properties)
      setHandoffRules(prev => [...prev, newRule])
      toast.success('Handoff rule created successfully')
    } catch (error) {
      console.error('Error adding handoff workflow to state:', error)
      // Refresh to get the correct state
      refreshWorkflows()
    }
  }

  // Update existing workflow
  const updateWorkflow = async (
    id: string,
    workflow: WorkflowWithActionGroups,
  ) => {
    try {
      // Optimistically update local state
      const updatedRule = transformWorkflowToHandoffRule(workflow, properties)
      setHandoffRules(prev =>
        prev.map(rule => (rule.id === id ? updatedRule : rule)),
      )
      toast.success('Handoff rule updated successfully')

      // Refresh to ensure we have the latest data from the server
      await refreshWorkflows()
    } catch (error) {
      console.error('Error updating handoff workflow:', error)
      toast.error('Failed to update handoff rule')
      // Refresh to get the correct state
      refreshWorkflows()
    }
  }

  // Delete workflow
  const deleteWorkflow = async (id: string) => {
    if (!confirm('Are you sure you want to delete this handoff rule?')) {
      return
    }

    try {
      const success = await workflowsApiClient.deleteWorkflow(id)
      if (success) {
        // Optimistically remove from local state
        setHandoffRules(prev => prev.filter(rule => rule.id !== id))
        toast.success('Handoff rule deleted successfully')
      } else {
        toast.error('Failed to delete handoff rule')
      }
    } catch (error) {
      console.error('Error deleting handoff workflow:', error)
      toast.error('Failed to delete handoff rule')
    }
  }

  // Duplicate workflow
  const duplicateWorkflow = async (id: string) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    try {
      const duplicatedWorkflow = isSuperAdmin
        ? await workflowsApiClient.duplicateAdminHandoffWorkflowForCompany(
            id,
            currentCompany.id,
          )
        : await workflowsApiClient.duplicateHandoffWorkflow(id)

      if (!duplicatedWorkflow) {
        toast.error('Failed to duplicate handoff rule')
        return
      }

      // Optimistically prepend new rule for immediate feedback
      const newRule = transformWorkflowToHandoffRule(
        duplicatedWorkflow,
        properties,
      )
      setHandoffRules(prev => [newRule, ...prev])
      toast.success('Handoff rule duplicated successfully')

      // Refresh to ensure ordering and latest server state
      await refreshWorkflows()
    } catch (error) {
      console.error('Error duplicating handoff workflow:', error)
      toast.error('Failed to duplicate handoff rule')
    }
  }

  // Helper to find rule by ID
  const findRuleById = (id: string): HandoffRule | undefined => {
    return handoffRules.find(rule => rule.id === id)
  }

  // Load workflows when company changes
  useEffect(() => {
    if (currentCompany?.id) {
      refreshWorkflows()
    }
  }, [currentCompany?.id])

  const contextValue: HandoffWorkflowsContextType = {
    // State
    handoffRules,
    loading,

    // Actions
    refreshWorkflows,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    duplicateWorkflow,

    // Helpers
    findRuleById,
  }

  return (
    <HandoffWorkflowsContext.Provider value={contextValue}>
      {children}
    </HandoffWorkflowsContext.Provider>
  )
}

// Hook to use the context
export function useHandoffWorkflows(): HandoffWorkflowsContextType {
  const context = useContext(HandoffWorkflowsContext)
  if (context === undefined) {
    throw new Error(
      'useHandoffWorkflows must be used within a HandoffWorkflowsProvider',
    )
  }
  return context
}

export type { HandoffWorkflowsContextType }
